import { paletteHelper, spacingHelper, Text, typographyHelper } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';
import { ContentBrick } from '../../../../../app-kit/components/ContentBrick/ContentBrick';
import { StyledQuestionTooltip } from '../../../../../app-kit/components/StyledQuestionTooltip/StyledQuestionTooltip';
import { formatPriceToShorthandPrice } from '../../../../../app-kit/helpers/prices';
import { getYearlyXAxisTicks } from '../../../../../components/TimeseriesAreaGraph/helpers/axes';
import {
  TimeSeriesAreaGraph,
  TimeseriesAreaGraph,
} from '../../../../../components/TimeseriesAreaGraph/TimeseriesAreaGraph';
import type { ChangeIcon } from './MediaCostChangeIcon';
import { MediaCostChangeIcon } from './MediaCostChangeIcon';

type ChangeVariant = 'NEGATIVE' | 'POSITIVE' | 'NEUTRAL';

interface MedianCostBrickProps {
  title: string;
  subtitle: string;
  disclaimer: string;
  cost: MedianCostValue;
  costChange?: MedianCostChange | null;
  graph: TimeSeriesAreaGraph;
  className?: string;
  renderGraph?: boolean;
  graphId: string;
}

interface MedianCostValue {
  title: string;
  suffix?: string | null;
}

export interface MedianCostChange {
  title: string;
  value: string;
  variant: ChangeVariant;
  iconNameV2: ChangeIcon | null;
}

const CostChange = styled.div`
  ${typographyHelper('body02')};
  color: ${paletteHelper('textSecondary')};
`;

const CostValue = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`;

const CostTitle = styled(Text)`
  line-height: 2rem;
`;

const CostSuffix = styled(Text)`
  margin-left: ${spacingHelper('twoExtraSmall')};
`;

const indicatorVariantStylingHelper = (variant: ChangeVariant) => {
  switch (variant) {
    case 'POSITIVE':
      return css`
        color: ${paletteHelper('textGreen')};
      `;
    case 'NEGATIVE':
      return css`
        color: ${paletteHelper('textPink')};
      `;
    case 'NEUTRAL':
      return css`
        color: ${paletteHelper('textPrimary')};
      `;
    default:
      return css`
        color: ${paletteHelper('textSecondary')};
      `;
  }
};

const TitleText = styled(Text)`
  ${typographyHelper('subtitle02')};
`;

const SubtitleText = styled(Text)`
  ${typographyHelper('subtitle02')};
  color: ${paletteHelper('textSecondary')};
  height: ${spacingHelper('twoExtraLarge')};
`;

const TrendIndicator = styled.span<{ $variant: ChangeVariant }>`
  ${typographyHelper('body02Bold')};
  margin-left: ${spacingHelper('twoExtraSmall')};
  display: inline-flex;
  align-items: center;
  padding-top: ${spacingHelper('twoExtraSmall')};
  ${(props) => indicatorVariantStylingHelper(props.$variant)};
`;

const TrendIcon = styled.span`
  margin-left: ${spacingHelper('twoExtraSmall')};
  display: inline-flex;
  align-items: center;
`;

const StyledMedianCostBrickGraph = styled(TimeseriesAreaGraph)`
  margin-top: ${spacingHelper('extraLarge')};
`;

const ResponsiveContainerHeight = 150;
export const EmptyMedianCostBrickGraph = styled.div`
  margin-top: ${spacingHelper('extraLarge')};
  height: ${ResponsiveContainerHeight}px;
`;

export const MedianCostBrick: FunctionComponent<MedianCostBrickProps> = ({
  title,
  subtitle,
  cost: { title: costTitle, suffix },
  costChange,
  disclaimer,
  graph,
  graphId,
  className,
  renderGraph,
}) => {
  return (
    <ContentBrick
      titleComponent={<TitleText as="h3">{title}</TitleText>}
      subtitleComponent={<SubtitleText>{subtitle}</SubtitleText>}
      headerChildren={
        <StyledQuestionTooltip inPageSource="property_page__median_price_brick___disclaimer_link">
          {disclaimer}
        </StyledQuestionTooltip>
      }
      isSubBrick={true}
      className={className}
    >
      <CostValue>
        <CostTitle variant="title02">{costTitle}</CostTitle>
        {suffix && <CostSuffix variant="subtitle01">{suffix}</CostSuffix>}
      </CostValue>
      {costChange && (
        <CostChange>
          {costChange.title}
          <TrendIndicator $variant={costChange.variant}>
            {costChange.value}
            {costChange.iconNameV2 && (
              <TrendIcon>
                <MediaCostChangeIcon iconName={costChange.iconNameV2} />
              </TrendIcon>
            )}
          </TrendIndicator>
        </CostChange>
      )}

      {renderGraph ? (
        graph && (
          <StyledMedianCostBrickGraph
            graph={graph}
            title={title}
            graphId={graphId}
            formatValueFn={formatPriceToShorthandPrice}
            formatXAxisValue={(value: string) => `${value.substring(0, 4)}`}
            getXAxisTicks={getYearlyXAxisTicks}
          />
        )
      ) : (
        <EmptyMedianCostBrickGraph />
      )}
    </ContentBrick>
  );
};
